import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseTextField from "../../../components/ui/BaseTextField";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import BaseRadio from "../../../components/ui/BaseRadio";
import { RadioGroup } from "@mui/material";
import {
  updateWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../store/workOrder/workOrderSlice";
import SearchLocation from "../../../components/SearchLocation/SearchLocation";
import { deleteLocation, fetchLocations } from "../../../services/api/location";
import { showToast } from "../../../store/toast/toastSlice";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { brandFontFamily } from "../../../utils/variables";

const LocationForm = forwardRef(({ isEditMode }, ref) => {
  const dispatch = useDispatch();
  const [locationType, setLocationType] = useState("EXACT_LOCATION");
  const location = useSelector((state) => state.workOrder.scheduling.location);
  const workOrderFormValidation = useSelector((state) => state.workOrder.workOrderFormValidation);

  const [selectedLocation, setSelectedLocation] = useState("");
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    isLoading: IsLocationsLoading,
    error: isLocationsError,
    data: locationsData,
    refetch: refetchLocations,
  } = useQuery({
    queryKey: ["locations"],
    queryFn: fetchLocations,
    enabled: locationType === "SAVED_LOCATION",
    refetchOnWindowFocus: false,
  });
  const {
    isPending: isDeletingLocation,
    error: deleteLocationError,
    mutateAsync: deleteLocationMutation,
  } = useMutation({
    mutationFn: deleteLocation,
    onSuccess: () => {
      dispatch(
        showToast({
          message: "Location deleted successfully",
          type: "success",
        }),
      );
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.message || "Failed to delete location",
          type: "error",
        }),
      );
    },
  });

  useImperativeHandle(ref, () => ({
    isValid: () => {
      const errors = [];

      const fail = (fieldName, message) => {
        errors.push(message);
        dispatch(updateWorkOrderFormValidation({ fieldName, data: true }));
      };
      const isBlank = (value) => value === "" || value === null || value === undefined;

      if (location.type === "EXACT_LOCATION") {
        if (isBlank(location.addressLine1)) {
          fail("addressLine1", "Address Line is required");
        } else if (isBlank(location.latitude) || isBlank(location.longitude)) {
          fail("addressLine1", "Search for an address so the location has coordinates");
        }
      } else if (location.type === "SAVED_LOCATION") {
        if (isBlank(location.addressId)) {
          fail("addressId", "Saved location is required");
        }
      }

      return { valid: errors.length === 0, errors };
    },
  }));

  const formattedLocation = locationsData?.data?.map((location) => ({
    label: location.displayName,
    value: location.id,
    address: location.addressLine1,
    timeZone: location.timeZone,
    latitude: location.latitude,
    longitude: location.longitude,
  }));
  useEffect(() => {
    if (!location) return;
    setLocationType(location?.type);
  }, [location.type]);

  useEffect(() => {
    const found = formattedLocation?.find((loc) => loc.value === location.addressId);
    setSelectedLocation(found || null);

    if (locationType === "SAVED_LOCATION" && formattedLocation && !found && location.addressId) {
      dispatch(
        updateWorkOrderForm({
          step: "scheduling",
          data: { location: { addressId: "" } },
        }),
      );
    }
  }, [formattedLocation, location.addressId, locationType]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    dispatch(
      updateWorkOrderForm({
        step: "scheduling",
        data: {
          location: {
            [name]: type === "checkbox" ? checked : value,
          },
        },
      }),
    );
    if (name === "addressId" && value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "addressId",
          data: false,
        }),
      );
      const selected = formattedLocation.find((loc) => loc.value === value);
      dispatch(
        updateWorkOrderForm({
          step: "scheduling",
          data: {
            schedule: { timeZone: selected?.timeZone || "UTC" },
            location: { latitude: selected?.latitude, longitude: selected?.longitude },
          },
        }),
      );
    } else if (name === "addressId" && !value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "addressId",
          data: true,
        }),
      );
    }
  };

  useEffect(() => {
    if (locationType === "SAVED_LOCATION") {
      console.log(`selectedLocation`, selectedLocation);
      dispatch(
        updateWorkOrderForm({
          step: "scheduling",
          data: {
            schedule: {
              timeZone: selectedLocation?.timeZone || "",
            },
          },
        }),
      );
    } else {
      dispatch(
        updateWorkOrderForm({
          step: "scheduling",
          data: {
            schedule: {
              timeZone: location.timeZone,
            },
          },
        }),
      );
    }
  }, [locationType]);

  useEffect(() => {
    if (location?.addressId && !isEditMode) {
      const filteredLocation = formattedLocation?.find(
        (address) => location.addressId === address.value,
      );

      dispatch(
        updateWorkOrderForm({
          step: "scheduling",
          data: {
            schedule: {
              timeZone: filteredLocation?.timeZone || "UTC",
            },
          },
        }),
      );
      setSelectedLocation(filteredLocation);
    }
  }, [location.addressId]);

  const handlePlaceSelected = (selectedAddress) => {
    console.log("selectedAddress", selectedAddress);
    dispatch(
      updateWorkOrderForm({
        step: "scheduling",
        data: {
          location: {
            country: selectedAddress.country,
            addressLine1: selectedAddress.completeAddress,
            city: selectedAddress.city,
            state: selectedAddress.state,
            zipCode: selectedAddress.zipCode,
            longitude: selectedAddress.longitude,
            latitude: selectedAddress.latitude,
            timeZone: selectedAddress.timeZone,
          },
        },
      }),
    );

    dispatch(
      updateWorkOrderForm({
        step: "scheduling",
        data: {
          schedule: {
            timeZone: selectedAddress.timeZone,
          },
        },
      }),
    );
  };

  const handleConfirmDeleteLocation = async () => {
    try {
      setIsDeleting(true);

      await deleteLocationMutation(locationToDelete.value);

      if (locationToDelete.value === location.addressId) {
        dispatch(
          updateWorkOrderForm({
            step: "scheduling",
            data: {
              location: {
                addressId: "",
              },
            },
          }),
        );
        setSelectedLocation(null);
      }
      setIsConfirmDeleteOpen(false);
      await refetchLocations();
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <BaseCard className="pt-4 pb-6 lg:px-16 px-5 mt-1 rounded-lg" elevation={1}>
      <div className="border-bottom mb-4">
        <h6 className="mb-2 text-dark">Location</h6>
      </div>
      <div className="mb-4">
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          value={location?.type}
          name="radio-buttons-group"
          onChange={(e) => {
            setLocationType(e.target.value);

            dispatch(
              updateWorkOrderForm({
                step: "scheduling",
                data: {
                  location: {
                    type: e.target.value,
                  },
                },
              }),
            );
          }}
        >
          <div>
            <BaseRadio label="Specify exact address" value="EXACT_LOCATION" />

            <BaseRadio label="Use saved location" value="SAVED_LOCATION" />

            <BaseRadio label="Remote (no address)" value="REMOTE" />
          </div>
        </RadioGroup>
      </div>
      <div>
        {location.type === "EXACT_LOCATION" && (
          <div className="grid grid-cols-2 gap-y-4">
            <div className="lg:mr-10 lg:col-span-1 col-span-2">
              <BaseTextField
                fullWidth
                fullHeight={false}
                label="Display name"
                hint="(The display name will be shared with the assigned provider)"
                optional={false}
                labelShrink={false}
                className="col-span-4"
                value={location.displayName}
                id="displayName"
                name="displayName"
                onChange={handleChange}
                isDefaultField={false}
                inputProps={{ maxLength: 100 }}
              />
            </div>
            <div className="lg:col-span-1 col-span-2">
              <BaseAutoSelect
                fullHeight={false}
                fullWidth
                label="Location type"
                labelShrink={false}
                placeholder="Location type"
                required={true}
                isDefaultField={false}
                options={[
                  { label: "Commercial", value: "COMMERCIAL" },
                  { label: "Residential", value: "RESIDENTIAL" },
                ]}
                id="locationType"
                name="locationType"
                value={location.locationType}
                onChange={handleChange}
              />
              {/* <BaseSelect
                fullHeight={false}
                fullWidth
                label="Location type"
                labelShrink={false}
                placeholder="Location type"
                required={true}
                isDefaultField={false}
                options={[
                  { label: "Commercial", value: "COMMERCIAL" },
                  { label: "Residential", value: "RESIDENTIAL" },
                ]}
                id="locationType"
                name="locationType"
                value={location.locationType}
                onChange={handleChange}
              /> */}
            </div>
            <div className="lg:mr-10 lg:col-span-1 col-span-2">
              {/* The only genuinely required field here — validation checks the
                  lat/long this populates. Everything else is derived from it. */}
              <SearchLocation onPlaceSelected={handlePlaceSelected} requirement="required" />
            </div>

            <div className="lg:col-span-1 col-span-2">
              <BaseTextField
                fullHeight={false}
                fullWidth
                label="Country"
                labelShrink={false}
                optional={false}
                /* options={[
              {
                label: "United States of America",
                value: "United States of America",
              },
            ]} */
                id="country"
                name="country"
                value={location.country}
                onChange={handleChange}
                isDefaultField={false}
              />
            </div>

            <div className="lg:mr-10 lg:col-span-1 col-span-2">
              <BaseTextField
                fullWidth
                fullHeight={false}
                label="Address Line"
                optional={false}
                requirement="required"
                labelShrink={false}
                id="addressLine1"
                name="addressLine1"
                value={location.addressLine1}
                onChange={handleChange}
                isDefaultField={false}
              />
            </div>

            <div className="lg:col-span-1 col-span-2">
              <BaseTextField
                fullWidth
                fullHeight={false}
                label="City"
                optional={false}
                labelShrink={false}
                id="city"
                name="city"
                value={location.city}
                onChange={handleChange}
                isDefaultField={false}
              />
            </div>

            <div className="lg:mr-10 lg:col-span-1 col-span-2">
              <BaseTextField
                label="State"
                fullHeight={false}
                labelShrink={false}
                optional={false}
                //   options={[{ label: "No project", value: null }]}
                id="state"
                name="state"
                value={location.state}
                onChange={handleChange}
                isDefaultField={false}
              />
            </div>

            <div className="lg:col-span-1 col-span-2">
              <BaseTextField
                fullWidth
                fullHeight={false}
                label="Zip Code"
                optional={false}
                labelShrink={false}
                isDefaultField={false}
                id="zipCode"
                name="zipCode"
                value={location.zipCode}
                onChange={handleChange}
              />
            </div>

            <div className="col-span-2">
              <BaseCheckbox
                label="Save this location for future use"
                id="saveForFuture"
                name="saveForFuture"
                checked={location.saveForFuture}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
        {location.type === "SAVED_LOCATION" && (
          <div className="grid grid-cols-2 gap-y-4">
            <div className="col-span-1 mr-10 mt-2">
              <BaseAutoSelect
                error={workOrderFormValidation.addressId}
                errorMsg={"Location is required"}
                fullWidth
                fullHeight={false}
                label="Saved Location"
                requirement="required"
                labelShrink={false}
                value={location.addressId}
                id="addressId"
                name="addressId"
                onChange={handleChange}
                renderOption={(props, option) => {
                  const { className, ...restProps } = props;
                  return (
                    <li
                      {...restProps}
                      className={`${className} flex text-sm items-centerjustify-between px-3 py-4 hover:bg-gray-100 cursor-pointer`}
                      style={{ fontWeight: "300", fontFamily: brandFontFamily }}
                    >
                      <span className="flex-1 truncate">{option.label}</span>
                      <div className="flex justify-end w-8">
                        <i
                          className="fa-solid fa-trash text-red-500 cursor-pointer hover:text-red-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLocationToDelete(option);
                            setIsConfirmDeleteOpen(true);
                          }}
                        ></i>
                      </div>
                    </li>
                  );
                }}
                options={formattedLocation?.map((loc) => ({
                  ...loc,
                  label: loc.label,
                }))}
                isDefaultField={false}
              />
            </div>

            {selectedLocation && (
              <div className="col-span-2">
                <p className="font-medium">
                  {selectedLocation.label}:{" "}
                  <span className="font-light	">{selectedLocation.address}</span>
                </p>
              </div>
            )}
          </div>
        )}
        {location.type === "REMOTE" && (
          <div className="grid grid-cols-2 gap-y-4">
            <p>This work order is to be completed remotely</p>
          </div>
        )}
        {isConfirmDeleteOpen && (
          <BaseConfirmModal
            open={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onConfirm={handleConfirmDeleteLocation}
            isLoading={isDeleting}
            title={`Are you sure you want to delete ${locationToDelete.label}?`}
            description="This action cannot be undone. The saved location will be permanently removed."
            confirmText="Delete"
            confirmButtonColor="error"
          />
        )}
      </div>
    </BaseCard>
  );
});

export default LocationForm;
